import { useEffect, useState } from "react";
import { useStation } from "@/context/StationContext";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { fmtL } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Droplets, AlertTriangle, Loader2, Save } from "lucide-react";

const card = "rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl";
const PRODUCT_COLORS = { RON95: "#059669", DIESEL: "#D97706", EURO5: "#2563EB" };
const statusMeta = {
  critical: { label: "Critical", bar: "bg-red-500", badge: "bg-red-500/15 text-red-400" },
  low: { label: "Low", bar: "bg-amber-500", badge: "bg-amber-500/15 text-amber-400" },
  ok: { label: "Healthy", bar: "bg-emerald-500", badge: "bg-emerald-500/15 text-emerald-400" },
};

export default function StockAlerts() {
  const { user } = useAuth();
  const { stationId: station, setStationId: setStation, stations } = useStation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pct, setPct] = useState("");
  const [varL, setVarL] = useState("");


  const load = () => {
    setLoading(true);
    const q = station === "all" ? "" : `?station_id=${station}`;
    api.get(`/stock${q}`).then((r) => { setData(r.data); setPct(String(r.data.low_stock_pct)); setVarL(String(r.data.stock_variance_l ?? 500)); }).finally(() => setLoading(false));
  };
  useEffect(load, [station]);

  const saveThreshold = async () => {
    try {
      await api.put("/settings", { low_stock_pct: Number(pct) });
      toast.success(`Alert threshold set to ${pct}%`);
      load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  const saveVarThreshold = async () => {
    try {
      await api.put("/settings", { stock_variance_l: Number(varL) });
      toast.success(`Variance alert set to ${varL} L`);
      load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-emerald-400 mb-1">Inventory</div>
          <h1 className="font-[Outfit] font-bold text-3xl sm:text-4xl tracking-tight">Stock &amp; Alerts</h1>
          <p className="text-[#9CA3AF] mt-1 text-sm">Remaining fuel = deliveries − sales, per product tank.</p>
        </div>
        <div>
          <Label className="text-[#9CA3AF] text-xs">Station</Label>
          <Select value={station} onValueChange={setStation}>
            <SelectTrigger data-testid="stock-station-select" className="mt-1.5 bg-[#111827] border-[#374151] text-white w-56"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#111827] border-[#374151] text-white">
              <SelectItem value="all">All Stations</SelectItem>
              {stations.map((st) => <SelectItem key={st.id} value={st.id}>{st.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading || !data ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-emerald-400 animate-spin" /></div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <div className={`${card} p-5`} data-testid="stock-critical-count">
              <div className="flex items-center gap-2 text-red-400 text-xs font-mono uppercase tracking-widest"><AlertTriangle className="w-4 h-4" /> Critical tanks</div>
              <div className="metric-value text-3xl font-bold mt-2">{data.critical}</div>
            </div>
            <div className={`${card} p-5`} data-testid="stock-low-count">
              <div className="flex items-center gap-2 text-amber-400 text-xs font-mono uppercase tracking-widest"><Droplets className="w-4 h-4" /> Low tanks</div>
              <div className="metric-value text-3xl font-bold mt-2">{data.low}</div>
            </div>
            <div className={`${card} p-5`}>
              <div className="text-[#9CA3AF] text-xs font-mono uppercase tracking-widest">Alert threshold</div>
              {user?.role === "manager" ? (
                <div className="flex items-center gap-2 mt-2">
                  <Input type="number" min="1" max="90" value={pct} onChange={(e) => setPct(e.target.value)}
                    data-testid="threshold-input" className="h-9 w-20 bg-[#0B0F17] border-[#374151] text-white metric-value" />
                  <span className="text-[#9CA3AF]">% of tank</span>
                  <Button size="sm" onClick={saveThreshold} data-testid="save-threshold-button" className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold ml-auto">
                    <Save className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="metric-value text-3xl font-bold mt-2">{data.low_stock_pct}%</div>
              )}
            </div>
            <div className={`${card} p-5`} data-testid="variance-threshold-card">
              <div className="text-[#9CA3AF] text-xs font-mono uppercase tracking-widest">Variance alert</div>
              {user?.role === "manager" ? (
                <div className="flex items-center gap-2 mt-2">
                  <Input type="number" min="0" step="50" value={varL} onChange={(e) => setVarL(e.target.value)}
                    data-testid="variance-threshold-input" className="h-9 w-24 bg-[#0B0F17] border-[#374151] text-white metric-value" />
                  <span className="text-[#9CA3AF]">L gap</span>
                  <Button size="sm" onClick={saveVarThreshold} data-testid="save-variance-threshold-button" className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold ml-auto">
                    <Save className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="metric-value text-3xl font-bold mt-2">{fmtL(data.stock_variance_l ?? 500)}</div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {data.items.map((it) => {
              const meta = statusMeta[it.status];
              return (
                <div key={`${it.station_id}-${it.product}`} className={`${card} p-5`} data-testid={`stock-tank-${it.station_id}-${it.product}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <span className="font-mono text-xs uppercase tracking-wider" style={{ color: PRODUCT_COLORS[it.product] }}>{it.product}</span>
                      <div className="text-sm text-[#9CA3AF] mt-0.5">{it.station}</div>
                    </div>
                    <span className={`text-[10px] font-mono px-2 py-1 rounded uppercase ${meta.badge}`}>{meta.label}</span>
                  </div>
                  <div className="mt-4">
                    <div className="flex items-end justify-between mb-1">
                      <span className="metric-value text-2xl font-bold text-white">{fmtL(Math.max(0, it.remaining))}</span>
                      <span className="text-xs text-[#9CA3AF]">of {fmtL(it.capacity)} · {it.pct}%</span>
                    </div>
                    <div className="h-2.5 rounded-full bg-[#1F2937] overflow-hidden">
                      <div className={`h-full ${meta.bar} transition-all`} style={{ width: `${it.pct}%` }} />
                    </div>
                    {it.remaining <= 0 && (
                      <div className="text-[11px] text-red-400 mt-1.5">Tank depleted — refill needed</div>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-4 text-xs">
                    <div className="rounded-lg bg-[#0B0F17] border border-[#1F2937] p-2">
                      <div className="text-[#9CA3AF]">Delivered</div>
                      <div className="metric-value text-blue-400 font-semibold">{fmtL(it.delivered)}</div>
                    </div>
                    <div className="rounded-lg bg-[#0B0F17] border border-[#1F2937] p-2">
                      <div className="text-[#9CA3AF]">Sold</div>
                      <div className="metric-value text-emerald-400 font-semibold">{fmtL(it.sold)}</div>
                    </div>
                  </div>
                </div>
              );
            })}
            {!data.items.length && <div className="text-sm text-[#9CA3AF]">No tanks configured yet.</div>}
          </div>
        </>
      )}
    </div>
  );
}
